/**
 * URL 보안 유틸리티 함수들
 */

/**
 * HTTP URL을 HTTPS로 변환
 * @param url - 변환할 URL
 * @returns HTTPS로 변환된 URL
 */
export const convertHttpToHttps = (url: string): string => {
  if (!url) return url

  const trimmed = url.trim()
  const hasScheme = /^[a-zA-Z][a-zA-Z\d+-.]*:/.test(trimmed)

  // 스킴이 없는 경우(로컬 경로)는 변환 대상이 아님
  if (!hasScheme) {
    return trimmed
  }

  try {
    const urlObj = new URL(trimmed)
    if (urlObj.protocol === "http:") {
      urlObj.protocol = "https:"
      return urlObj.href
    }
    return trimmed
  } catch {
    // URL 파싱 실패 시 원본 반환
    return trimmed
  }
}

/**
 * URL이 유효한지 검증
 * @param url - 검증할 URL
 * @returns 유효한 URL인지 여부
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || url.trim() === "") return false

  // 상대 경로나 data URL은 유효한 것으로 간주
  if (
    url.startsWith("/") ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  ) {
    return true
  }

  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * URL이 HTTPS인지 확인 (상대 경로와 data URL은 안전한 것으로 간주)
 * @param url - 확인할 URL
 * @returns HTTPS 또는 안전한 URL인지 여부
 */
export const isSecureUrl = (url: string): boolean => {
  if (!url) return false

  const trimmed = url.trim()

  // 상대 경로나 data URL은 안전한 것으로 간주
  if (
    trimmed.startsWith("/") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:")
  ) {
    return true
  }

  // 프로토콜 상대 URL은 허용하지 않음
  if (trimmed.startsWith("//")) {
    return false
  }

  const hasScheme = /^[a-zA-Z][a-zA-Z\d+-.]*:/.test(trimmed)

  // 스킴이 없는 경우는 로컬 경로로 간주하여 허용
  if (!hasScheme) {
    return true
  }

  try {
    const urlObj = new URL(trimmed)
    return urlObj.protocol === "https:"
  } catch {
    return false
  }
}

/**
 * 안전한 이미지 URL 반환 (HTTP를 HTTPS로 변환하거나 폴백 사용)
 * @param url - 원본 URL
 * @param fallback - 폴백 URL (기본값: /images/placeholder.png)
 * @param enforceHttps - HTTPS 강제 여부 (기본값: true)
 * @returns 안전한 이미지 URL
 */
export const getSecureImageUrl = (
  url: string,
  fallback: string = "/images/placeholder.png",
  enforceHttps: boolean = true
): string => {
  if (!url || url.trim() === "") {
    return fallback
  }

  const trimmed = url.trim()

  // 상대 경로나 data URL은 그대로 사용
  if (
    trimmed.startsWith("/") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:")
  ) {
    return trimmed
  }

  if (!/^[a-zA-Z][a-zA-Z\d+-.]*:/.test(trimmed)) {
    return trimmed
  }

  try {
    const urlObj = new URL(trimmed)

    // HTTP를 HTTPS로 변환
    if (enforceHttps && urlObj.protocol === "http:") {
      urlObj.protocol = "https:"
      return urlObj.href
    }

    return urlObj.href
  } catch {
    // URL 파싱 실패 시 폴백 이미지 사용
    return fallback
  }
}
